import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { ModuleFilter } from "../../../utils/ModuleFilter";
import { TableToolbar } from "../../../utils/TableToolbar";
import { useTableView } from "../../../utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../utils/PMTableView/RightDrawer";
import {
  embeddedTableKey,
  mergeScopedFilters,
  useScopeFiltersSync,
  computeHasMore,
  embeddedFetchKey,
  scopeFiltersKey,
} from "../../../utils/PMTableView/embedded-table-utils";
import { CustomerTitle, ProjectTitle, dateToString } from "../../../Utils";
import { getChecklistsAsync } from "../../../../slices/quality/checklist";
import {
  ChecklistsDataTable,
  getChecklistColumns,
} from "./checklistsTable";
import {
  ChecklistApprovedStatus,
  ChecklistStatus,
  checklistStatusOptions,
} from "./utils";

const CHECKLIST_LIST_FIELDS =
  "id,number,displayTitle,title,description,project,customer,checklist_type,folder,status,approved,approved_by,responsible_executive,created_at";

export const ChecklistStacklist = (props) => {
  const { filters: scopeFilters = [], module: moduleName = "embedded" } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const itemState = useSelector((store) => store.checklists);
  const { isLoading, totalCount } = itemState;

  const loadMoreInFlightRef = useRef(false);
  const [drawerItem, setDrawerItem] = useState(null);

  const [controller, setController] = useState(() => ({
    filters: mergeScopedFilters(scopeFilters, []),
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 25,
    query: "",
    sort: props.sort || "desc",
    sortBy: props.sortBy || "updated_at",
    view: "all",
    fields: props.fields || CHECKLIST_LIST_FIELDS,
  }));

  useScopeFiltersSync(scopeFilters, setController);

  const columns = useMemo(() => getChecklistColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: embeddedTableKey("checklists", moduleName),
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || c.filters,
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    const userFilters = [];
    if (filters.checklistStatuses?.length > 0) {
      userFilters.push({
        name: "status",
        value: JSON.stringify(filters.checklistStatuses),
      });
    }
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: mergeScopedFilters(scopeFilters, userFilters),
    }));
  };

  const getInitialFilters = () => {
    const initial = { query: controller.query || "", checklistStatuses: [] };
    controller.filters.forEach((filter) => {
      if ((scopeFilters || []).some((sf) => sf.name === filter.name)) return;
      if (filter.name !== "status") return;
      try {
        const values = JSON.parse(filter.value);
        initial.checklistStatuses = Array.isArray(values)
          ? values.map(String)
          : [String(values)];
      } catch {
        initial.checklistStatuses = [String(filter.value)];
      }
    });
    return initial;
  };

  const handleLoadMore = useCallback(() => {
    const loaded = itemState?.data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = (controller.page || 1) + 1;
    loadMoreInFlightRef.current = true;
    dispatch(
      getChecklistsAsync({ ...controller, page: nextPage, append: true })
    ).finally(() => {
      loadMoreInFlightRef.current = false;
    });
    setController((c) => ({ ...c, page: nextPage }));
  }, [
    controller,
    dispatch,
    isLoading,
    itemState?.data?.length,
    totalCount,
  ]);

  const debouncedFetch = useMemo(
    () => debounce((ctrl) => dispatch(getChecklistsAsync(ctrl)), 250),
    [dispatch]
  );

  const filtersKey = scopeFiltersKey(controller.filters);
  const fetchKey = useMemo(
    () => embeddedFetchKey(controller),
    [
      filtersKey,
      controller.query,
      controller.sort,
      controller.sortBy,
      controller.per_page,
      controller.view,
      controller.fields,
    ]
  );

  useEffect(() => {
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    debouncedFetch({ ...controller, page: 1 });
    return () => debouncedFetch.cancel();
  }, [fetchKey, debouncedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLength = itemState?.data?.length || 0;
  const hasMore = computeHasMore({ isLoading, dataLength, totalCount });

  return (
    <div className="min-w-0 space-y-4">
      <ModuleFilter
        filterCard
        disabled={isLoading}
        onFiltersChange={handleFiltersChange}
        initialFilters={getInitialFilters()}
        searchPlaceholder={t("Search checklists...")}
        filterTitle={t("Filter Checklists")}
        hideStatusFilter
        customDropdownFilters={[
          {
            key: "checklistStatuses",
            label: t("Statuses"),
            options: checklistStatusOptions.map((status) => ({
              value: String(status.value),
              label: status.label,
            })),
          },
        ]}
      />

      <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
        <TableToolbar
          groupOptions={groupOptions}
          activeGroup={tableView.groupBy}
          onGroupChange={tableView.setGroupBy}
          columns={columnMeta}
          columnOrder={tableView.order}
          visibleKeys={tableView.visibleKeys}
          onToggleColumn={tableView.toggleColumn}
          onReorderColumn={tableView.reorderColumn}
          onResetColumns={tableView.resetColumns}
          savedViews={tableView.views}
          activeSavedViewId={tableView.activeViewId}
          savedViewDirty={tableView.dirty}
          onApplySavedView={tableView.applyView}
          onSaveAsView={tableView.saveAsView}
          onUpdateView={tableView.updateView}
          onDeleteView={tableView.deleteView}
          density={tableView.density}
          onDensityChange={tableView.setDensity}
        />
        <ChecklistsDataTable
          columns={columns}
          visibleKeys={tableView.visibleKeys}
          widths={tableView.widths}
          onResize={tableView.resizeColumn}
          groupBy={tableView.groupBy}
          sort={tableSort}
          onSort={handleSort}
          itemState={itemState}
          density={tableView.density}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onRowClick={setDrawerItem}
          layout="cards"
          hideSelection
        />
      </div>

      <RightDrawer
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        eyebrow={drawerItem ? `${t("Number")} ${drawerItem.number || ""}` : ""}
        title={drawerItem?.displayTitle || drawerItem?.title || t("Checklist")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () =>
              navigate(`/dashboard/hse-ks/checklists/${drawerItem.id}`),
          },
        ]}
      >
        {drawerItem && (
          <DrawerSection>
            <DrawerProps
              items={[
                {
                  label: t("Status"),
                  value: <ChecklistStatus checklist={drawerItem} />,
                },
                {
                  label: t("Type"),
                  value: drawerItem.checklist_type?.displayTitle || "—",
                },
                {
                  label: t("project"),
                  value: (
                    <ProjectTitle
                      project={drawerItem?.project}
                      folder={drawerItem?.folder}
                    />
                  ),
                },
                {
                  label: t("Customer"),
                  value: <CustomerTitle customer={drawerItem?.customer} />,
                },
                {
                  label: t("Created"),
                  value: drawerItem.created_at
                    ? dateToString(drawerItem.created_at)
                    : "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>
    </div>
  );
};

ChecklistStacklist.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "updated_at",
};
